/**
 * Runs one bulk action over the selection, ONE ELEMENT AT A TIME.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Sequential is not a simplification, it is the correctness requirement. Every
 * per-element guard on the server is judged against the pool as it stands at
 * that moment: capacity, and the "keep at least N live" floor. Fire five
 * demotes at once and each one individually looks safe against a pool that
 * still had five live elements — the floor is breached by the batch, not by any
 * member of it. In sequence each call sees the previous one's effect, so the
 * floor stops the batch exactly where it should.
 *
 * One failure does not abort the rest: the elements are independent, and the
 * user gets a per-element verdict instead of "stopped somewhere, unclear what
 * happened". Cancel stops before the NEXT call, never mid-flight.
 *
 * Nothing is invalidated per element — the caller refreshes once at the end.
 * Refetching this queue N times would re-read the tracker and rebuild the
 * metrics cube for every row.
 */

import { useCallback, useRef, useState } from 'react';
import type { QueueElement } from '@trafficpulse/shared';

import { getApiErrorMessage } from '@/lib/api-error';

export interface BulkItemResult {
  element: QueueElement;
  ok: boolean;
  /** The server's own reason when it refused — never a generic string. */
  message?: string;
}

export interface BulkRunState {
  running: boolean;
  done: number;
  total: number;
  results: BulkItemResult[];
  /**
   * The element being written RIGHT NOW. The batch is strictly sequential and a
   * single write plus its refetch can take seconds, so without this the rows
   * sit unchanged and the run reads as "nothing is happening".
   */
  activeBankId: string | null;
}

const IDLE: BulkRunState = {
  running: false,
  done: 0,
  total: 0,
  results: [],
  activeBankId: null,
};

export function useBulkRunner() {
  const [state, setState] = useState<BulkRunState>(IDLE);
  const cancelled = useRef(false);

  const cancel = useCallback(() => {
    cancelled.current = true;
  }, []);

  const reset = useCallback(() => setState(IDLE), []);

  const run = useCallback(
    async (
      items: QueueElement[],
      perform: (el: QueueElement) => Promise<unknown>,
    ): Promise<BulkItemResult[]> => {
      cancelled.current = false;
      setState({
        running: true,
        done: 0,
        total: items.length,
        results: [],
        activeBankId: null,
      });
      const results: BulkItemResult[] = [];
      for (const element of items) {
        if (cancelled.current) break;
        setState((prev) => ({ ...prev, activeBankId: element.bankId }));
        try {
          await perform(element);
          results.push({ element, ok: true });
        } catch (e) {
          results.push({ element, ok: false, message: getApiErrorMessage(e) });
        }
        setState((prev) => ({
          ...prev,
          done: results.length,
          results: [...results],
        }));
      }
      setState((prev) => ({
        ...prev,
        running: false,
        activeBankId: null,
        results: [...results],
      }));
      return results;
    },
    [],
  );

  return { ...state, run, cancel, reset };
}
